import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StudentSection } from "./StudentLayout";
import { Check, Clock, ScanLine, TriangleAlert } from "lucide-react";

/**
 * QR self check-in page.
 * Landed here when a student scans the academy QR at the door. Shows every
 * class currently within the check-in window (15 min before → 30 min after
 * start). One tap → mark themselves in/late for the picked class.
 */
export default function StudentSelfCheckin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/student/checkin/options").then(r => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const checkIn = async (klass) => {
    setBusy(klass.class_id);
    try {
      const r = await api.post("/student/checkin", { class_id: klass.class_id, date: data.date });
      setResult(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not check in. Ask your coach.");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <div className="p-8 label">Loading…</div>;

  if (result) {
    return (
      <div data-testid="page-self-checkin-success">
        <StudentSection section="Check-in" title="You're in" />
        <div className="px-5 mt-4">
          <div className="card p-6 text-center">
            <div className="mx-auto mb-4 rounded-full flex items-center justify-center"
                 style={{ width: 64, height: 64,
                          background: result.status === "late" ? "rgba(244,185,66,0.15)" : "var(--green-bg)",
                          border: `1px solid ${result.status === "late" ? "rgba(244,185,66,0.35)" : "var(--green-border)"}` }}>
              <Check size={30} style={{ color: result.status === "late" ? "rgb(244,185,66)" : "var(--green)" }} />
            </div>
            <div className="text-[22px] font-bold uppercase tracking-tight" data-testid="checkin-result-title">
              {result.status === "late" ? "Marked late" : "Checked in"}
            </div>
            <div className="label-tiny mt-1">
              {result.class_name} · {result.start_time}
            </div>
            <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }}>
              Have a good roll! Oss 🤝
            </div>
            <button onClick={() => nav("/student/home")}
                    className="btn btn-outline w-full mt-5"
                    data-testid="checkin-done-btn">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-self-checkin">
      <StudentSection section="Check-in" title={`Hi ${(data.student.name || "").split(" ")[0]}`} />
      <div className="px-5 mt-2 label-tiny">
        {data.now} · {data.options.length === 0 ? "no classes open right now"
          : `${data.options.length} class${data.options.length === 1 ? "" : "es"} open for check-in`}
      </div>

      <div className="px-5 mt-4 space-y-3">
        {data.options.length === 0 && (
          <div className="card p-6 text-center" data-testid="checkin-no-classes">
            <TriangleAlert size={26} className="mx-auto mb-2" style={{ color: "var(--text-dim)" }} />
            <div className="font-semibold text-[14px] uppercase tracking-tight">Nothing open</div>
            <div className="label-tiny mt-2" style={{ color: "var(--text-dim)" }}>
              Check-in opens 15 minutes before class and closes 30 minutes after start. Try again closer to your class time.
            </div>
          </div>
        )}
        {data.options.map(k => {
          const isDone = !!k.already_checked_in;
          return (
            <div key={k.class_id} className="card p-4 flex items-center gap-3" data-testid={`checkin-option-${k.class_id}`}>
              <div className="mono text-[15px] font-semibold w-16">{k.start_time}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] uppercase tracking-tight truncate">{k.name}</div>
                <div className="label-tiny mt-0.5 flex items-center gap-1">
                  <Clock size={11} />
                  {k.will_be_marked === "late"
                    ? `You'll be marked LATE (${k.window_delta_min}m past start)`
                    : k.window_delta_min < 0
                      ? `Starts in ${Math.abs(k.window_delta_min)} min`
                      : `On time · ${k.window_delta_min}m in`}
                </div>
              </div>
              {isDone ? (
                <span className="pill" data-testid={`checkin-already-${k.class_id}`}
                      style={{ background: "var(--green-bg)", color: "var(--green)", border: "1px solid var(--green-border)" }}>
                  <Check size={11} /> {k.already_checked_in}
                </span>
              ) : (
                <button onClick={() => checkIn(k)} disabled={busy === k.class_id}
                        className="btn btn-primary"
                        style={{ padding: "8px 14px", fontSize: 12 }}
                        data-testid={`checkin-btn-${k.class_id}`}>
                  {busy === k.class_id ? "…" : "Check in"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-5 mt-6 mb-3 label-tiny text-center" style={{ color: "var(--text-dim)" }}>
        <ScanLine size={13} className="inline mr-1 mb-0.5" />
        You scanned the academy QR — this only works while you're at the gym.
      </div>
    </div>
  );
}
